import { assert, definedMap } from "@glideapps/ts-necessities";
import type {
    ActionPropertyDescriptor,
    AppDescriptionContext,
    ComponentBuilder,
    ComponentDescriptor,
    SchemaInspector,
} from ".";
import type { SerializedComponent } from "../../common/components/SerializedApp";
import { logError } from "../../common/debug-print";
import type {
    ActionComponentDescription,
    ActionDescription,
    ActionKind,
    ComponentKind,
    Description,
    InputOutputTables,
    LegacyPropertyDescription,
    MutatingScreenKind,
    PropertyDescription,
    TableColumn,
    TableGlideType,
} from "../../common/description";
import {
    getTableColumn,
    getTableColumnDisplayName,
    isPrimitiveType,
    makeEmptyComponentDescription,
    PropertyKind,
} from "../../common/description";
import { getDocURL } from "../../common/docUrl";
import { getDefaultPrimitiveActionKinds } from "../../component-utils/primitive-actions";
import { defaultActionForColumn, makeScreenContextForActions } from "../actions";
import { tokenForProperty } from "../actions/base";
import { ListItemFlags } from "../array-screens/list-item-flags";
import { handleActionIcon, iconForActions, makeText, withCaption } from "../builder-utils";
import { getActionsForComponent } from "./component-utils";
import { makeCaptionStringPropertyDescriptor, makeTextPropertyDescriptor } from "./descriptor-utils";
import { ComponentHandlerBase } from "./handler";
import { SwitchPropertyHandler } from "./property-handlers";
import { PropertySection } from "./property-sections";
import {
    getColumnProperty,
    getIconProperty,
    makeActionProperty,
    makeColumnProperty,
    makeStringProperty,
    makeSwitchProperty,
} from "./property-utils";

export { getDefaultPrimitiveActionKinds, getPrimitiveActionKinds } from "../../component-utils/primitive-actions";

const ComponentKindPrimitive: ComponentKind = "primitive";

const componentName = "Action Text";

interface PrimitiveComponentDescription extends ActionComponentDescription {
    readonly propertyName: LegacyPropertyDescription;
    readonly caption: LegacyPropertyDescription | undefined;
    readonly truncate: PropertyDescription | undefined;
    readonly actionIcon: PropertyDescription | undefined;
}

const truncatePropertyHandler = new SwitchPropertyHandler(
    {
        truncate: false,
    },
    "Trim text to first 4 lines",
    PropertySection.Options
);

export function makeDefaultActionDescriptorWithKinds(
    kinds: readonly ActionKind[],
    defaultAction: boolean | ((table: TableGlideType, desc: Description) => ActionDescription | undefined)
): ActionPropertyDescriptor {
    return {
        property: { name: "actions" },
        kind: PropertyKind.Action,
        label: "Action",
        kinds,
        defaultAction,
        section: PropertySection.Action,
    };
}

export function makeDefaultActionDescriptor(
    schema: SchemaInspector | undefined,
    mutatingScreenKind: MutatingScreenKind | undefined,
    defaultAction: boolean | ((table: TableGlideType, desc: Description) => ActionDescription | undefined),
    extraKinds?: readonly ActionKind[]
): ActionPropertyDescriptor {
    const kinds = [...(extraKinds ?? []), ...getDefaultPrimitiveActionKinds(schema, mutatingScreenKind)];
    return makeDefaultActionDescriptorWithKinds(kinds, defaultAction);
}

export class PrimitiveComponentHandler extends ComponentHandlerBase<PrimitiveComponentDescription> {
    constructor() {
        super(ComponentKindPrimitive);
    }

    public get needsColumns(): boolean {
        return false;
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: PrimitiveComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;

        const text = makeText(builder, desc.propertyName, tables.input, true, true, true);
        if (text === undefined) return b.emptyUI;

        const { actions } = getActionsForComponent(this, desc, tables.input, builder, mutatingScreenKind);
        const { screenContext, lets } = makeScreenContextForActions(builder, actions, tables, mutatingScreenKind);

        lets.push(["text", text]);

        const onTap = builder.makeSequenceFromActions(
            actions,
            tables,
            screenContext,
            outputContext,
            mutatingScreenKind,
            false
        );

        const actionIcon = getIconProperty(desc.actionIcon);
        const iconName = actionIcon ?? definedMap(actions, iconForActions);
        const truncate = truncatePropertyHandler.getSwitch(desc);

        let icon: SerializedComponent | undefined;
        let flags = ListItemFlags.WrapText | ListItemFlags.InvertTitleAndSubtitle | ListItemFlags.ConvertNewlines;
        if (truncate) {
            flags |= ListItemFlags.TruncateWrap;
        }
        if (iconName === undefined) {
            flags |= ListItemFlags.DisableChevron;
        } else if (typeof iconName === "string") {
            icon = b.constant(iconName);
        }

        if (actions.length === 0) {
            flags |= ListItemFlags.LightWeight;
        }

        return withCaption(builder, tables.input, desc.caption, lets, caption => {
            return handleActionIcon(b, onTap, icon, false, (ot, ic) =>
                b.ifIsNotEmpty(
                    b.getVariable("text"),
                    b.adaptiveListItem(
                        b.getVariable("text"),
                        caption,
                        undefined,
                        undefined,
                        ic,
                        undefined,
                        ot,
                        undefined,
                        undefined,
                        b.constant(flags),
                        undefined,
                        undefined,
                        undefined
                    )
                )
            );
        });
    }

    public getDescriptor(
        desc: PrimitiveComponentDescription | undefined,
        table: TableGlideType | undefined,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor {
        const actions = definedMap(desc, d => getActionsForComponent(this, d, table, ccc, mutatingScreenKind))?.actions;
        let icon = definedMap(actions, iconForActions);
        if (icon === true) icon = undefined;
        return {
            name: componentName,
            description: "A labeled value",
            img: "componentActionText",
            group: "Text",
            helpUrl: getDocURL("actionText"),
            properties: [
                makeTextPropertyDescriptor("propertyName", "Text", "Enter text", true, mutatingScreenKind, {
                    isMultiLine: true,
                    preferredNames: ["text"],
                    columnFirst: true,
                    isDefaultCaption: true,
                }),
                makeCaptionStringPropertyDescriptor("Data", false, mutatingScreenKind),
                truncatePropertyHandler,
                {
                    kind: PropertyKind.Icon,
                    property: { name: "actionIcon" },
                    label: "Icon",
                    defaultIcon: icon,
                    section: PropertySection.Design,
                },
                ...this.getBasePropertyDescriptors(),
            ],
        };
    }

    public getActionDescriptors(
        _desc: PrimitiveComponentDescription | undefined,
        _table: TableGlideType | undefined,
        schema: SchemaInspector | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): readonly ActionPropertyDescriptor[] {
        return [
            makeDefaultActionDescriptor(schema, mutatingScreenKind, (t, d) => {
                const p = d as PrimitiveComponentDescription;
                const columnProperty = getColumnProperty(p.propertyName);
                if (columnProperty === undefined) return undefined;

                const column = getTableColumn(t, columnProperty);
                if (column === undefined) {
                    logError("Could not find column for table for primitive action", columnProperty, t);
                    return undefined;
                }
                return defaultActionForColumn(column);
            }),
        ];
    }

    public static defaultComponent(column: TableColumn): PrimitiveComponentDescription {
        assert(isPrimitiveType(column.type));

        const action = defaultActionForColumn(column);
        return {
            ...makeEmptyComponentDescription(ComponentKindPrimitive),
            propertyName: makeColumnProperty(column.name),
            caption: makeStringProperty(getTableColumnDisplayName(column)),
            actions: definedMap(action, makeActionProperty),
            truncate: makeSwitchProperty(false),
            actionIcon: undefined,
        };
    }

    public getDescriptiveName(
        desc: PrimitiveComponentDescription,
        tables: InputOutputTables,
        ccc: AppDescriptionContext
    ): [string, string] {
        return [componentName, tokenForProperty(desc.propertyName, tables, ccc)?.value ?? ""];
    }
}
